import { useMemo } from 'react';
import { useMMKVString } from 'react-native-mmkv';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import { getNativeStorage } from 'src/lib/storage';
import { parseJSON } from 'src/utils/json';
export default function usePersistedState(storageKey, defaultValue) {
    const storage = getNativeStorage(storageKey.scope);
    const [json, setJson] = useMMKVString(storageKey.key, storage);
    // useMMKVObject JSON.parses and throws on corrupt / non-JSON values
    // (e.g. a raw JWT from older code), which permanently crashes the app.
    // Memoize on the raw string so consumers can use the value as an effect
    // dependency. Parsing on every render handed out a new object each time
    // and re-fired those effects forever.
    const state = useMemo(() => (typeof json === 'string' ? parseJSON(json) : undefined), [json]);
    useEffectWithCtx(ctx => {
        if (typeof ctx.json !== 'string') {
            return;
        }
        try {
            JSON.parse(ctx.json);
        }
        catch {
            ctx.storage.remove(ctx.key);
        }
    }, { json, storage, key: storageKey.key }, [json]);
    const setState = useStableCallback(v => {
        if (v instanceof Function) {
            setJson(currentJson => {
                const currentValue = typeof currentJson === 'string'
                    ? parseJSON(currentJson)
                    : undefined;
                const newValue = v(currentValue);
                return newValue != null ? JSON.stringify(newValue) : undefined;
            });
        }
        else {
            setJson(v != null ? JSON.stringify(v) : undefined);
        }
    });
    return [state ?? defaultValue, setState];
}
